import React, { Component } from 'react';
import { connect } from 'react-redux';
import ReactDOM from 'react-dom';
import PropTypes from 'prop-types';
import Autosuggest from 'react-autosuggest';
import match from 'autosuggest-highlight/match';
import parse from 'autosuggest-highlight/parse';
import {
  find, reduce, without, concat, indexOf, delay, compact,
  throttle,
} from 'lodash';
import Fa from 'react-fontawesome';
import { CSSTransition, TransitionGroup } from 'react-transition-group';
import { parallel } from 'async';

import masterField from './master-field';
import Avatar from '../avatar';
import styles from './styles.css';
import { fetchFilterValues } from './search-form-actions';
import { getSearchObjects, getSearchFilters } from './search-form-reducer';

function escapeRegexCharacters(str) {
  return str.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

function blurAll(parent = document.body) {
  const tmp = document.createElement('input');
  tmp.className = styles.hiddenInput;
  parent.appendChild(tmp);
  delay(() => {
    tmp.focus();
    parent.removeChild(tmp);
  }, 1);
}
const FadeAnimation = ({ children, ...props }) => (
  <CSSTransition
    {...props}
  >
    {children}
  </CSSTransition>
);
FadeAnimation.propTypes = {
  children: PropTypes.element,
};
FadeAnimation.defaultProps = {
  children: null,
};

class DropDownField extends Component {
  constructor(props) {
    super(props);
    this.state = {
      value: '',
      loading: false,
      loadingValues: false,
      focus: false,
      suggestions: [],
      error: null,
    };
    this.onChange = this.onChange.bind(this);
    this.onFocus = this.onFocus.bind(this);
    this.onBlur = this.onBlur.bind(this);
    this.onSuggestionsFetchRequested = throttle(this.onSuggestionsFetchRequested, 500, { leading: false }).bind(this);
    this.onSuggestionsClearRequested = this.onSuggestionsClearRequested.bind(this);
    this.onSuggestionSelected = this.onSuggestionSelected.bind(this);
    this.getSuggestionValue = this.getSuggestionValue.bind(this);
    this.getSectionSuggestions = this.getSectionSuggestions.bind(this);
    this.removeTag = this.removeTag.bind(this);
    this.renderSuggestion = this.renderSuggestion.bind(this);
    this.renderSectionTitle = this.renderSectionTitle.bind(this);
    this.renderSuggestionsContainer = this.renderSuggestionsContainer.bind(this);
  }
  
  componentDidMount() {
    this.mounted = true;
  }

  componentWillUnmount() {
    this.mounted = false;
  }

  onChange(event, { newValue }) {
    this.setState({
      value: newValue,
    });
  }

  onFocus() {
    this.setState({ focus: true });
  }

  onBlur() {
    this.setState({ focus: false });
  }

  // Autosuggest will call this function every time
  // you need to update suggestions.
  onSuggestionsFetchRequested({ value }) {
    this.setState({ loading: true, suggestions: [] });
    const { searchCriteria, searchType, isOpportunityId } = this.props;
    const currentFilter = searchCriteria.filter;
    const escapedValue = escapeRegexCharacters(value.trim());
    const regex = new RegExp(`${escapedValue}`, 'i');

    const p = new Promise((resolve, reject) => {
      parallel(
        [
          (callback) => {
            if (isOpportunityId) {
              callback(null);
            } else {
              const currentMappings = searchCriteria.mappings || [];
              const availableMappings = searchCriteria.filter.mappings || [];
              const mappingMatches = reduce(availableMappings, (items, mapping) => {
                const matchId = indexOf(currentMappings, mapping._id) >= 0;
                const matchKeyword = escapedValue === '' ? true : regex.test(mapping.label);
                if (!matchId && matchKeyword) {
                  items.push({ ...mapping, tagType: 'mapping' });
                }
                return items;
              }, []);
              if (mappingMatches.length) {
                callback(null, {
                  title: 'Filter Groups',
                  suggestions: mappingMatches,
                });
              } else {
                callback(null);
              }
            }
          },
          (callback) => {
            if (escapedValue && escapedValue.length) {
              const title = (currentFilter.meta || { label: 'Values' }).label;
              if (isOpportunityId) {
                const currentValues = searchCriteria.values || [];
                const exist = indexOf(currentValues, escapedValue) >= 0;
                if (!exist) {
                  const matched = escapedValue.match(/[0-9]{11}|[a-zA-Z0-9]{15}|[a-zA-Z0-9]{18}|[a-zA-Z0-9\\-]{36}/);
                  if (matched) {
                    callback(null, {
                      title,
                      suggestions: [{
                        tagType: 'value',
                        _id: escapedValue,
                        value: escapedValue,
                        label: escapedValue,
                        name: escapedValue,
                        isId: true,
                      }],
                    });
                  } else {
                    callback(null, {
                      title,
                      suggestions: [{
                        isEmpty: true,
                        label: (<span>no results found for <code>{escapedValue}</code></span>),
                      }],
                    });
                  }
                } else {
                  callback(null, {
                    title,
                    suggestions: [],
                  });
                }
              } else if (!this.state.loadingValues) {
                this.setState({ loadingValues: true }, () => {
                  this.props.dispatch(fetchFilterValues(searchType, currentFilter._id, escapedValue, currentFilter.is_dynamic))
                    .then(
                      (availableValues) => {
                        this.setState({ loadingValues: false, error: null });
                        const currentValues = searchCriteria.values || [];
                        const valueMatches = reduce(availableValues, (items, val) => {
                          const matchId = indexOf(currentValues, val.value) >= 0;
                          const matchKeyword = escapedValue === '' ? true : regex.test(val.label);
                          if (!matchId && matchKeyword) {
                            items.push({ ...val, tagType: 'value' });
                          }
                          return items;
                        }, []);
                        if (valueMatches.length) {
                          callback(null, {
                            title,
                            // title: escapedValue,
                            suggestions: valueMatches,
                          });
                        } else {
                          callback(null, {
                            title,
                            suggestions: [{
                              isEmpty: true,
                              label: (<span>no results found for <code>{escapedValue}</code></span>),
                            }],
                          });
                        }
                      },
                      (e) => {
                        this.setState({ error: e.data ? e.data.message || e.data : e });
                        callback(null);
                      },
                    );
                });
              } else {
                callback(null);
              }
            } else {
              callback(null, {
                title: (currentFilter.meta || { label: 'Values' }).label,
                suggestions: [{
                  isEmpty: true,
                  label: 'type to search...',
                }],
              });
            }
          },
        ],
        (error, results) => {
          if (error) {
            reject(error);
          } else {
            resolve(compact(results));
          }
        },
      );
    });

    p.then(
      (suggestions) => {
        this.setState({ suggestions, loading: false });
      },
      () => {
        this.setState({ suggestions: [], loading: false });
      },
    );
    return p;
  }

  // Autosuggest will call this function every time you need to clear suggestions.
  onSuggestionsClearRequested() {
    this.setState({ suggestions: [] });
  }

  onSuggestionSelected(event, { suggestion }) {
    if (!suggestion.isEmpty && !suggestion.isHeader) {
      const { searchCriteria, path } = this.props;
      if (suggestion.tagType === 'mapping') {
        const mappings = searchCriteria.mappings || [];
        const value = suggestion._id;
        const exist = indexOf(mappings, value) >= 0;
        const newMappings = exist ? without(mappings, value) : concat(mappings, value);
        this.props.onUpdate(path, { mappings: newMappings });
      } else {
        const values = searchCriteria.values || [];
        const { value } = suggestion;
        const exist = indexOf(values, value) >= 0;
        const newValues = exist ? without(values, value) : concat(values, value);
        this.props.onUpdate(path, { values: newValues });
      }
    }
    this.setState({ value: '' });
    blurAll(this.element);
  }


  getSuggestionValue(suggestion) {
    if (suggestion.isHeader || suggestion.isEmpty) {
      return '';
    }
    return suggestion.label;
  }

  getSectionSuggestions(section) {
    return section.suggestions;
  }

  removeTag(suggestion) {
    const { searchCriteria, path } = this.props;
    if (suggestion.tagType === 'mapping') {
      const mappings = searchCriteria.mappings || [];
      const newMappings = without(mappings, suggestion._id);
      this.props.onUpdate(path, { mappings: newMappings });
    } else {
      const values = searchCriteria.values || [];
      const newValues = without(values, suggestion.value);
      this.props.onUpdate(path, { values: newValues });
    }
  }

  renderSuggestion(suggestion, { query }) {
    const { label } = suggestion;
    if (suggestion.isEmpty) {
      return (
        <a href="" onClick={(e) => { e.preventDefault(); e.stopPropagation(); }} style={{ cursor: 'not-allowed' }}>
          <em className="text-muted">{suggestion.label}</em>
        </a>
      );
    }
    const matches = match(label, query);
    const parts = parse(label, matches).map((part, id) => ({ ...part, id }));
    const isUser = suggestion.user_id !== undefined && suggestion.user_id !== null;
    
    const labelClass = [];
    if (suggestion.isId) {
      labelClass.push(styles.idTag);
    }
    if (suggestion.is_active === false) {
      labelClass.push('text-muted');
    }

    return (
      <a href="" onClick={(e) => { e.preventDefault(); }}>
        {isUser && <Avatar small user={suggestion} />}
        <span className={labelClass.join(' ')}>
          {parts.map((part) => {
            const className = part.highlight ? styles.tagHighlight : null;
            return (
              <span className={className} key={part.id}>
                {part.text}
              </span>
            );
          })}
        </span>
      </a>
    );
  }

  renderSuggestionsContainer({ containerProps, children }) {
    if (this.props.dropdownRef && this.element) {
      const bounds = this.element.getBoundingClientRect();
      const containerBounds = this.props.dropdownRef.getBoundingClientRect();

      const { height, left, top } = bounds;
      const elY = top + document.documentElement.scrollTop;
      const elX = left + document.documentElement.scrollLeft;
      
      const containerX = containerBounds.left + document.documentElement.scrollLeft;
      const containerY = containerBounds.top + document.documentElement.scrollTop;

      const y = elY - containerY;
      const x = elX - containerX;


      const inner = (
        <div {...containerProps} style={{ top: (y + height), left: x }}>
          {children}
        </div>
      );

      return ReactDOM.createPortal(
        inner,
        this.props.dropdownRef,
      );
    }
    return (
      <div {...containerProps}>
        {children}
      </div>
    );
  }

  renderSectionTitle(section) {
    return section.title ? `${section.title}:` : null;
  }

  render() {
    const {
      value, suggestions, focus, loading, error,
    } = this.state;
    const {
      disabled, searchCriteria, objects, searchFilter,
      isOpportunityId, readOnly,
    } = this.props;
    if (!searchFilter) {
      return null;
    }
    const inputProps = {
      placeholder: 'search...',
      value,
      className: styles.tagInput,
      onChange: this.onChange,
      onFocus: this.onFocus,
      onBlur: this.onBlur,
      disabled,
    };
    const theme = {
      container: styles.autocomplete,
      suggestionsContainer: `dropdown open ${styles.suggestionsContainer}`,
      sectionContainer: `dropdown-menu ${styles.sectionContainer}`,
      suggestionsList: `dropdown-menu ${styles.suggestionsList}`,
      sectionTitle: 'dropdown-header',
      suggestion: '',
      suggestionFocused: 'active',
    };

    const mappingTags = reduce(searchCriteria.mappings, (items, val) => {
      const v = find(searchFilter.mappings, { _id: val });
      if (v) {
        items.push({ ...v, tagType: 'mapping' });
      }
      return items;
    }, []);

    const valueTags = reduce(searchCriteria.values, (items, val) => {
      if (isOpportunityId) {
        items.push({
          _id: val, value: val, label: val, tagType: 'value', isId: true,
        });
      } else {
        const v = find(searchFilter.values, { value: val });
        if (v) {
          items.push({ ...v, tagType: 'value' });
        } else if (searchCriteria.error) {
          items.push({ value: val, label: val, tagType: 'value' });
        } else {
          const o = find(objects, { value: val });
          if (o) {
            items.push({ ...o, tagType: 'value' });
          }
        }
      }
      return items;
    }, []);

    const tags = concat(mappingTags, valueTags);

    const hasMoreMappings = searchFilter.mappings && mappingTags.length < searchFilter.mappings.length;
    let hasMoreValues = true;
    /*
    if (searchFilter.$has_deferred_values) {
      hasMoreValues = searchFilter.options === undefined || (searchFilter.options && valueTags.length < searchFilter.options.length);
    } else if (searchFilter.values) {
      hasMoreValues = valueTags.length < searchFilter.values.length;
    } else {
      hasMoreValues = true;
    }
    */
    const hasMore = hasMoreMappings || hasMoreValues;
    // const isUser = searchFilter.$is_user_mapping;
    const cls = [styles.tagInputField];
    if (focus) {
      cls.push(styles.tagInputFieldFocus);
    }
    if (loading) {
      cls.push(styles.tagInputFieldLoading);
    }
    if (readOnly) {
      cls.push(styles.tagInputFieldReadOnly);
    }
    const hasStatus = find(tags, (t) => {
      return t.is_active === true || t.is_active === false;
    });

    let missingValues = null;
    if (searchCriteria.invalidValues && searchCriteria.invalidValues.length > 0) {
      missingValues = (
        <div className="text-danger">
          <p>
            <Fa name="exclamation-triangle" /> Values no longer exists: {searchCriteria.invalidValues.map((iv, i) => {
              return [
                (<code key={iv}>{iv}</code>),
                ...(i < searchCriteria.invalidValues.length - 1 ? [(<span key={`${iv}-space`}>,</span>)] : []),
              ];
            })}
          </p>
        </div>
      );
    }

    let missingMappings = null;
    if (searchCriteria.invalidMappings && searchCriteria.invalidMappings.length > 0) {
      missingMappings = (
        <div className="text-danger">
          <p>
            <Fa name="exclamation-triangle" /> Mappings no longer exists: {searchCriteria.invalidMappings.map((iv, i) => {
              return [
                (<code key={iv}>{iv}</code>),
                ...(i < searchCriteria.invalidMappings.length - 1 ? [(<span key={`${iv}-space`}>,</span>)] : []),
              ];
            })}
          </p>
        </div>
      );
    }

    return (
      <div>
        <div
          className={`${cls.join(' ')}`}
        >
          <TransitionGroup className={styles.tagList}>
            {tags.map((tag) => {
              const isUserTag = tag.user_id !== undefined && tag.user_id !== null;

              const tCls = [styles.tag];
              if (tag.tagType === 'mapping') {
                tCls.push(styles.mappingTag);
              } else if (tag.tagType === 'value') {
                tCls.push(styles.valueTag);
              }
              if (isUserTag) {
                tCls.push(styles.userTag);
              }
              if (tag.isId) {
                tCls.push(styles.idTag);
              }
              if (disabled) {
                tCls.push(styles.disabledTag);
              }
              if (readOnly) {
                tCls.push(styles.readOnlyTag);
              }
              
              if (hasStatus && tag.is_active === false) {
                tCls.push('text-muted');
              }
              
              return (
                <FadeAnimation
                  classNames="tag"
                  timeout={500}
                  key={tag.tagType === 'mapping' ? tag._id : tag.value}
                >
                  <span className={`${tCls.join(' ')}`}>
                    {isUserTag && <Avatar small user={tag} />}
                    <span className={styles.tagLabel}>{tag.label}</span>
                    {readOnly && <span>&nbsp;&nbsp;</span>}
                    {!readOnly &&
                      <a
                        href=""
                        className={styles.tagTrash}
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          if (!disabled) {
                            this.removeTag(tag);
                          }
                        }}
                      >
                        <Fa name="times" />
                      </a>
                    }
                  </span>
                </FadeAnimation>
              );
            })}
            {hasMore && !disabled &&
              <FadeAnimation
                classNames="tag"
                timeout={500}
              >
                <div ref={(el) => { this.element = el; }} className={styles.autocompleteWrapper}>
                  <Autosuggest
                    theme={theme}
                    suggestions={suggestions}
                    multiSection
                    renderSectionTitle={this.renderSectionTitle}
                    getSectionSuggestions={this.getSectionSuggestions}
                    onSuggestionsFetchRequested={this.onSuggestionsFetchRequested}
                    shouldRenderSuggestions={() => true}
                    onSuggestionsClearRequested={this.onSuggestionsClearRequested}
                    onSuggestionSelected={this.onSuggestionSelected}
                    getSuggestionValue={this.getSuggestionValue}
                    renderSuggestion={this.renderSuggestion}
                    renderSuggestionsContainer={this.renderSuggestionsContainer}
                    inputProps={inputProps}
                    ref={(input) => { this.fieldEl = input; }}
                  />
                  <Fa name="spinner" spin className={styles.spinner} />
                </div>
              </FadeAnimation>
            }
          </TransitionGroup>
        </div>
        {error !== null && error !== undefined &&
          <div className="text-danger">{error}</div>
        }
        {missingMappings}
        {missingValues}
      </div>
    );
  }
}
function mapStateToProps(state, props) {
  const { searchCriteria, searchType } = props;
  const { filter } = searchCriteria;
  // const isOpportunityId = targetObject === 'Opportunity' && filter.target_field === 'Id';
  const isOpportunityId = filter.$is_opportunity_id
  const objects = getSearchObjects(searchType, state);

  const searchFilter = find(getSearchFilters(searchType, state), { _id: filter._id })
  return {
    objects: objects[filter._id] || [],
    isOpportunityId,
    searchFilter,
  };
}

DropDownField.propTypes = {
  dispatch: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
  objects: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  path: PropTypes.arrayOf(PropTypes.string).isRequired,
  searchType: PropTypes.string.isRequired,
  searchFilter: PropTypes.shape({}).isRequired,
  searchCriteria: PropTypes.shape({
    _id: PropTypes.string.isRequired,
    mappings: PropTypes.arrayOf(PropTypes.string),
    values: PropTypes.arrayOf(PropTypes.string),
    error: PropTypes.string,
    invalidValues: PropTypes.arrayOf(PropTypes.string),
    invalidMappings: PropTypes.arrayOf(PropTypes.string),
    filter: PropTypes.shape({
      _id: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
    }),
  }),
  onUpdate: PropTypes.func.isRequired,
  isOpportunityId: PropTypes.bool,
  dropdownRef: PropTypes.oneOfType([
    // Either a function
    PropTypes.func,
    // Or the instance of a DOM native element (see the note about SSR)
    PropTypes.shape({ current: PropTypes.elementType }),
  ]),
};

DropDownField.defaultProps = {
  searchCriteria: null,
  disabled: false,
  isOpportunityId: false,
  dropdownRef: null,
};


export default masterField(connect(mapStateToProps)(DropDownField));
